import React, { useState } from 'react'
import { Input, InputNumber, Button, Tag, Tooltip, Card } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { FormGrid, FormField, BadgeContainer } from './shared'
import { useFieldPath, useTabContext } from './TabContext'
import { get } from 'lodash'

const { TextArea } = Input

export const ListingMetadataSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<any>()
  const { currentTab } = useTabContext()

  // Get field paths for current tab
  const thumbnailImagePath = useFieldPath('thumbnailImage')
  const originalPricePath = useFieldPath('originalPrice')
  const finalPricePath = useFieldPath('finalPrice')
  const featuredOrderPath = useFieldPath('featuredOrder')
  const courseNamePath = useFieldPath('courseName')
  const urlSlugPath = useFieldPath('urlSlug')
  const videoIdPath = useFieldPath('videoId')
  const customDescriptionPath = useFieldPath('customDescription')
  const promoTextPath = useFieldPath('promoText')
  const badgesPath = useFieldPath('badges')
  const pixDiscountPercentagePath = useFieldPath('pixDiscountPercentage')

  const badges = get(values, badgesPath, [])
  const defaultPixDiscountPercentage = get(values, 'default.pixDiscountPercentage', 0)
  const pixDiscountPercentage = get(
    values,
    pixDiscountPercentagePath,
    currentTab === 'DEFAULT' ? 0 : undefined
  )
  const [newBadge, setNewBadge] = useState('')

  const addBadge = () => {
    if (newBadge && !badges.includes(newBadge)) {
      setFieldValue(badgesPath, [...badges, newBadge])
      setNewBadge('')
    }
  }

  const removeBadge = (badge: string) => {
    setFieldValue(
      badgesPath,
      badges.filter((b) => b !== badge)
    )
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldValue(e.target.name, e.target.value)
  }

  const handlePixDiscountChange = (value: number | string | null) => {
    setFieldValue(pixDiscountPercentagePath, value === null ? undefined : Number(value))
  }

  return (
    <Card>
      <FormGrid>
        <FormField>
          <label>
            <Tooltip title="URL da imagem miniatura para listagem de cursos">
              Imagem Thumbnail
            </Tooltip>
          </label>
          <Input
            name={thumbnailImagePath}
            value={get(values, thumbnailImagePath, '')}
            onChange={handleChange}
            placeholder="https://exemplo.com/thumbnail.jpg"
          />
        </FormField>

        <FormField>
          <label>
            <Tooltip title="Preço original antes do desconto">Preço Original</Tooltip>
          </label>
          <Input
            name={originalPricePath}
            type="number"
            step="0.01"
            value={get(values, originalPricePath, '')}
            onChange={handleChange}
            placeholder="Ex: 299.90"
          />
        </FormField>

        <FormField>
          <label>
            <Tooltip title="Preço final após desconto">Preço Final</Tooltip>
          </label>
          <Input
            name={finalPricePath}
            type="number"
            step="0.01"
            value={get(values, finalPricePath, '')}
            onChange={handleChange}
            placeholder="Ex: 149.90"
          />
        </FormField>

        <FormField>
          <label>
            <Tooltip title="Ordem de destaque na página (números menores aparecem primeiro)">
              Ordem de Destaque
            </Tooltip>
          </label>
          <Input
            name={featuredOrderPath}
            type="number"
            value={get(values, featuredOrderPath, '')}
            onChange={handleChange}
            placeholder="Ex: 1, 2, 3..."
          />
        </FormField>
      </FormGrid>
      <FormField>
        <label>
          <Tooltip title="Percentual de desconto aplicado quando o aluno escolhe Pix. Use 0 para desativar.">
            Desconto Pix (%)
          </Tooltip>
        </label>
        <InputNumber
          name={pixDiscountPercentagePath}
          min={0}
          max={50}
          step={1}
          precision={0}
          value={pixDiscountPercentage}
          onChange={handlePixDiscountChange}
          placeholder={
            currentTab === 'DEFAULT'
              ? '0'
              : `Herdar padrão (${defaultPixDiscountPercentage || 0}%)`
          }
          style={{ width: 180 }}
        />
      </FormField>

      <FormField>
        <label>
          <Tooltip title="Nome do curso para o site">Nome do Curso</Tooltip>
        </label>
        <TextArea
          name={courseNamePath}
          value={get(values, courseNamePath, '')}
          onChange={handleChange}
          rows={2}
          placeholder="Nome do curso para o site"
        />
      </FormField>

      <FormField>
        <label>
          <Tooltip title="Slug do link do curso para o site">Slug do Curso</Tooltip>
        </label>
        <TextArea
          name={urlSlugPath}
          value={get(values, urlSlugPath, '')}
          onChange={handleChange}
          rows={1}
          placeholder="Slug do link do curso para o site"
        />
      </FormField>

      <FormField>
        <label>
          <Tooltip title="Video ID do curso para o site">Video ID do curso</Tooltip>
        </label>
        <TextArea
          name={videoIdPath}
          value={get(values, videoIdPath, '')}
          onChange={handleChange}
          rows={1}
          placeholder="Video ID do curso para o site"
        />
      </FormField>

      <FormField>
        <label>
          <Tooltip title="Descrição customizada para o site (substitui a descrição padrão)">
            Descrição Customizada
          </Tooltip>
        </label>
        <TextArea
          name={customDescriptionPath}
          value={get(values, customDescriptionPath, '')}
          onChange={handleChange}
          rows={3}
          placeholder="Descrição personalizada do curso para o site"
        />
      </FormField>

      <FormField>
        <label>
          <Tooltip title="Texto promocional especial">Texto Promocional</Tooltip>
        </label>
        <TextArea
          name={promoTextPath}
          value={get(values, promoTextPath, '')}
          onChange={handleChange}
          rows={2}
          placeholder="Ex: Aproveite esta oferta especial!"
        />
      </FormField>

      <FormField>
        <label>
          <Tooltip title="Badges/etiquetas que aparecem no curso (ex: 'Novo', 'Popular', 'Limitado')">
            Badges
          </Tooltip>
        </label>
        <BadgeContainer>
          <div style={{ marginBottom: 8 }}>
            {badges.map((badge) => (
              <Tag key={badge} closable onClose={() => removeBadge(badge)}>
                {badge}
              </Tag>
            ))}
          </div>
          <Input.Group compact>
            <Input
              style={{ width: 'calc(100% - 32px)' }}
              value={newBadge}
              onChange={(e) => setNewBadge(e.target.value)}
              onPressEnter={addBadge}
              placeholder="Digite um badge e pressione Enter"
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={addBadge} />
          </Input.Group>
        </BadgeContainer>
      </FormField>
    </Card>
  )
}
